import * as React from 'react'
import { createRootRouteWithContext, Outlet, useRouterState, useNavigate } from '@tanstack/react-router'
// import { TanStackRouterDevtools } from '@tanstack/router-devtools'
import type { QueryClient } from '@tanstack/react-query'
import { Sidebar } from '@/components/layout/sidebar'
import { SidebarProvider, useSidebar } from '@/providers/sidebar-provider'
import { BranchProvider } from '@/providers/branch-provider'
import { AuthProvider, useAuth } from '@/providers/auth-provider'

interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
})

// Routes that don't require authentication and don't show sidebar
const PUBLIC_ROUTES = ['/login', '/change-password']

function MainContent() {
  const { isCollapsed } = useSidebar()

  return (
    <>
      {/* Spacer div that takes up sidebar width on desktop */}
      <div
        className="hidden lg:block flex-shrink-0 transition-all duration-300"
        style={{ width: isCollapsed ? 68 : 266 }}
      />
      {/* Main content area */}
      <div className="flex-1 min-w-0 min-h-screen">
        <Outlet />
      </div>
    </>
  )
}

function AuthenticatedLayout() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading, user } = useAuth()

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({ to: '/login' })
    }
  }, [isAuthenticated, isLoading, navigate])

  // Redirect to change-password if user must change password
  React.useEffect(() => {
    if (!isLoading && isAuthenticated && user?.mustChangePassword) {
      navigate({ to: '/change-password' })
    }
  }, [isLoading, isAuthenticated, user, navigate])

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-page">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-text-muted">Loading...</p>
        </div>
      </div>
    )
  }

  // Don't render anything if not authenticated (redirect will happen)
  if (!isAuthenticated) {
    return null
  }

  return (
    <BranchProvider>
      <SidebarProvider>
        <div className="flex min-h-screen bg-page">
          <Sidebar />
          <MainContent />
        </div>
      </SidebarProvider>
    </BranchProvider>
  )
}

function PublicLayout() {
  return <Outlet />
}

function RootLayout() {
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname

  // Check if current route is public
  const isPublicRoute = PUBLIC_ROUTES.some((route) => currentPath.startsWith(route))

  return (
    <AuthProvider>
      {isPublicRoute ? <PublicLayout /> : <AuthenticatedLayout />}
      {/* {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />} */}
    </AuthProvider>
  )
}
